import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer } from "lucide-react";
import { Bill } from "@/types";
import { format } from "date-fns";

interface BillViewerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bill: Bill;
  patientName: string;
}

export function BillViewerDialog({ isOpen, onClose, bill, patientName }: BillViewerDialogProps) {
  const handlePrint = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.print();
      return;
    }

    // Create the print HTML
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill - ${bill.billNumber}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 10px 20px;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              background: white;
            }
            
            .bill-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
            }
            
            .clinic-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .clinic-details {
              font-size: 11px;
              color: #666;
              margin-bottom: 5px;
            }
            
            .patient-bill-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 11px;
            }
            
            .patient-info {
              flex: 1;
              margin-right: 20px;
            }
            
            .bill-info {
              flex: 1;
              text-align: right;
            }
            
            .services-section {
              margin-bottom: 20px;
            }
            
            .service-header {
              display: grid;
              grid-template-columns: 1fr auto;
              gap: 10px;
              font-weight: bold;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #ccc;
            }
            
            .service-item {
              display: grid;
              grid-template-columns: 1fr auto;
              gap: 10px;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .total-section {
              margin-top: 20px;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .total-row.final {
              font-weight: bold;
              font-size: 12px;
              border-top: 1px solid #000;
              padding-top: 5px;
              margin-top: 10px;
            }
            
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ccc;
              font-size: 10px;
              color: #666;
            }
            
            .amount {
              text-align: right;
            }
            
            .due-amount {
              color: #dc2626;
              font-weight: bold;
            }
            
            .fully-paid {
              color: #16a34a;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="bill-header">
            <div class="clinic-name">${bill.clinicDetails.name}</div>
            <div class="clinic-details">${bill.clinicDetails.address}</div>
            <div class="clinic-details">Phone: ${bill.clinicDetails.phone} | Email: ${bill.clinicDetails.email}</div>
            ${bill.clinicDetails.website ? `<div class="clinic-details">Website: ${bill.clinicDetails.website}</div>` : ''}
          </div>
          
          <div class="patient-bill-info">
            <div class="patient-info">
              <div><span class="label">Name:</span> ${patientName}</div>
              <div><span class="label">Bill No:</span> ${bill.billNumber}</div>
            </div>
            <div class="bill-info">
              <div><span class="label">Date:</span> ${format(new Date(bill.billDate), "dd MMM yyyy")}</div>
            </div>
          </div>
          
          <div class="services-section">
            <div class="service-header">
              <div>Service</div>
              <div class="amount">Amount (₹)</div>
            </div>
            ${bill.services.map((service) => `
              <div class="service-item">
                <div>${service.service_name}</div>
                <div class="amount">${(Number(service.price) || 0).toFixed(2)}</div>
              </div>
            `).join('')}
          </div>
          
          <div class="total-section">
            <div class="total-row">
              <span>Total Amount</span>
              <span class="amount">₹${bill.totalAmount.toFixed(2)}</span>
            </div>
            ${bill.discountAmount > 0 ? `
              <div class="total-row">
                <span>Discount</span>
                <span class="amount">-₹${bill.discountAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="total-row final">
              <span>Final Total</span>
              <span class="amount">₹${(bill.totalAmount - bill.discountAmount).toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Amount Paid</span>
              <span class="amount">₹${bill.amountPaid.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Payment Mode</span>
              <span class="amount">${bill.paymentMode}</span>
            </div>
            <div class="total-row final ${bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? 'fully-paid' : 'due-amount'}">
              <span>${bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? 'Fully Paid' : 'Due Amount'}</span>
              <span class="amount">₹${Math.abs((bill.totalAmount - bill.discountAmount) - bill.amountPaid).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Thank you for choosing ${bill.clinicDetails.name}</p>
            <p>This is a computer generated receipt</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const getStatusBadge = () => {
    const isFullyPaid = bill.amountPaid >= (bill.totalAmount - bill.discountAmount);
    
    if (bill.status === 'cancelled') {
      return <Badge variant="destructive">Cancelled</Badge>;
    } else if (bill.status === 'refunded') {
      return <Badge variant="secondary">Refunded</Badge>;
    } else if (isFullyPaid) {
      return <Badge variant="default">Paid</Badge>;
    } else {
      return <Badge variant="outline">Pending</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <DialogTitle>Bill - {bill.billNumber}</DialogTitle>
              <DialogDescription>
                Generated on {format(new Date(bill.createdAt), "dd MMM yyyy, HH:mm")}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge()}
              <Button onClick={handlePrint} variant="outline">
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 p-4">
          {/* Header */}
          <div className="flex justify-between items-start border-b pb-2">
            <div>
              <h2 className="text-lg font-bold">{bill.clinicDetails.name}</h2>
              <p className="text-sm text-muted-foreground">{bill.clinicDetails.address}</p>
              <p className="text-sm text-muted-foreground">Phone: {bill.clinicDetails.phone}</p>
              <p className="text-sm text-muted-foreground">Email: {bill.clinicDetails.email}</p>
              {bill.clinicDetails.website && (
                <p className="text-sm text-muted-foreground">Website: {bill.clinicDetails.website}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">Date: {format(new Date(bill.billDate), "dd MMM yyyy")}</p>
              <p className="text-sm font-medium">Time: {format(new Date(bill.billDate), "HH:mm")}</p>
              <p className="text-sm font-medium">Bill No: {bill.billNumber}</p>
            </div>
          </div>

          {/* Patient Info */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>
              <span className="ml-1 font-medium">{patientName}</span>
            </div>
          </div>

          {/* Services and Charges */}
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="font-medium">Service</div>
              <div className="font-medium text-right">Amount (₹)</div>
            </div>
            
            {/* Services */}
            {bill.services.length > 0 && (
              <div className="space-y-2">
                {bill.services.map((service) => (
                  <div key={service.id} className="grid grid-cols-2 gap-2 text-sm">
                    <div>{service.service_name}</div>
                    <div className="text-right">{(Number(service.price) || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Payment Summary */}
            <div className="space-y-1">
              {/* Total Amount */}
              <div className="grid grid-cols-2 gap-2 text-sm font-medium border-t pt-1">
                <div>Total Amount</div>
                <div className="text-right">{bill.totalAmount.toFixed(2)}</div>
              </div>

              {/* Discount */}
              {bill.discountAmount > 0 && (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Discount</div>
                  <div className="text-right">-{bill.discountAmount.toFixed(2)}</div>
                </div>
              )}
              
              <div className="flex justify-between font-medium">
                <span>Final Total:</span>
                <span>₹{(bill.totalAmount - bill.discountAmount).toFixed(2)}</span>
              </div>

              {/* Amount Paid */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Amount Paid</div>
                <div className="text-right">{bill.amountPaid.toFixed(2)}</div>
              </div>

              {/* Payment Mode */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Payment Mode</div>
                <div className="text-right capitalize">{bill.paymentMode}</div>
              </div>

              {/* Amount Due */}
              <div className="grid grid-cols-2 gap-2 text-sm font-medium border-t pt-1">
                <div className={bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? "text-green-600" : "text-red-600"}>
                  {bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? "Fully Paid" : "Due Amount"}
                </div>
                <div className={`text-right ${bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? "text-green-600" : "text-red-600"}`}>
                  {Math.abs((bill.totalAmount - bill.discountAmount) - bill.amountPaid).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground border-t pt-2">
            <p>Thank you for choosing {bill.clinicDetails.name}</p>
            <p>This is a computer generated receipt</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 